import { Invoice, useGroupAdmin } from '@intly/core';
import { Form, FormInput, FormSwitch, InvoiceFormItem } from '@intly/design';
import { Box, Divider, MenuItem, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { DateTime } from 'luxon';
import { FC, useMemo } from 'react';

export const invoiceStatusOptions = [
  'draft',
  'unpaid',
  'paid',
  'overdue',
  'cancelled',
  'refunded',
  'partially_paid',
];

const InvoiceEdit: FC = () => {
  const {
    isEditInvoice,
    editInvoice,
    updateInvoice,
    createInvoice,
    isUpdatingInvoice,
    isAddingInvoice,
    setIsAddingInvoice,
    group,
  } = useGroupAdmin();

  const handleSubmit = (values: any) => {
    if (isEditInvoice?.id) {
      if (isEditInvoice?.status !== 'paid' && values.status === 'paid') {
        values.paymentDate = DateTime.now().toFormat('yyyy-MM-dd');
      }
      updateInvoice(values);
    } else {
      createInvoice(values);
    }
  };

  const defaultValues = useMemo(
    () =>
      isEditInvoice?.id
        ? isEditInvoice
        : {
            invoiceNumber: '',
            invoiceDate: DateTime.now().toFormat('yyyy-MM-dd'),
            dueDate: DateTime.now().plus({ days: 30 }).toFormat('yyyy-MM-dd'),
            customerNumber: '',
            invoicePeriodStart: DateTime.now().toFormat('yyyy-MM-dd'),
            invoicePeriodEnd: DateTime.now()
              .plus({ days: 30 })
              .toFormat('yyyy-MM-dd'),
            status: 'draft',
            paymentDate: null,
            amount: 0,
            amountNet: 0,
            vat: 0,
            discount: 0,
            currency: 'SEK',
            billing: group?.billing ?? {
              isPrivate: false,
              companyName: '',
              orgNo: '',
              reference: '',
              email: '',
              firstName: '',
              lastName: '',
              address: '',
              postalCode: '',
              city: '',
            },
          },
    [isEditInvoice, group]
  );

  return (
    <Box>
      <Form
        defaultValues={defaultValues}
        values={defaultValues}
        onSubmit={handleSubmit}
        onCancel={() => {
          if (isAddingInvoice) {
            setIsAddingInvoice(false);
          }
          editInvoice(null);
        }}
        loading={isUpdatingInvoice}
      >
        <Grid2 container spacing={2}>
          <Grid2 xs={12} sm={4}>
            {Boolean(isEditInvoice?.id) && (
              <FormInput
                fullWidth
                name="invoiceNumber"
                label="Invoice number"
              />
            )}
          </Grid2>

          <Grid2 xs={12} sm={4}>
            <FormInput fullWidth name="invoiceDate" label="Invoice date" />
          </Grid2>

          <Grid2 xs={12} sm={4}>
            <FormInput fullWidth name="dueDate" label="Due date" />
          </Grid2>

          <Grid2 xs={12} sm={4}>
            {Boolean(isEditInvoice?.id) && (
              <FormInput
                fullWidth
                name="customerNumber"
                label="Customer number"
              />
            )}
          </Grid2>

          <Grid2 xs={12} sm={4}>
            <FormInput
              fullWidth
              name="invoicePeriodStart"
              label="Period start"
            />
          </Grid2>

          <Grid2 xs={12} sm={4}>
            <FormInput fullWidth name="invoicePeriodEnd" label="Period end" />
          </Grid2>

          <Grid2 xs={12} sm={4}>
            <FormInput fullWidth select name="status" label="Status">
              {invoiceStatusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </FormInput>
          </Grid2>

          <Grid2 xs={12} sm={4}>
            <FormInput fullWidth name="paymentDate" label="Payment date" />
          </Grid2>

          <Grid2 xs={12} sm={4} />

          <Grid2 xs={12} sm={3}>
            <FormInput fullWidth name="amount" label="Amount" type="number" />
          </Grid2>
          <Grid2 xs={12} sm={3}>
            <FormInput
              fullWidth
              name="amountNet"
              label="Net amount"
              type="number"
            />
          </Grid2>
          <Grid2 xs={12} sm={3}>
            <FormInput fullWidth name="vat" label="Vat" type="number" />
          </Grid2>
          <Grid2 xs={12} sm={3}>
            <FormInput
              fullWidth
              name="discount"
              label="Discount"
              type="number"
            />
          </Grid2>
          <Grid2 xs={12} sm={3}>
            <FormInput
              fullWidth
              name="currency"
              label="Currency"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid2>
        </Grid2>

        <InvoiceFormItem name="items" />

        <Divider sx={{ mt: 2 }} />

        <Typography sx={{ my: 2 }}>Billing info</Typography>
        <Grid2 container spacing={2}>
          <Grid2 xs={12}>
            <FormSwitch name="billing.isPrivate" label="PRIVATE_PERSON" />
          </Grid2>

          <Grid2 xs={12} sm={6}>
            <FormInput
              fullWidth
              name="billing.companyName"
              label="COMPANY_NAME"
            />
          </Grid2>

          <Grid2 xs={12} sm={6}>
            <FormInput
              fullWidth
              name="billing.orgNo"
              label="ORG_NO"
              helperText="xxxxxx-xxxx"
            />
          </Grid2>

          <Grid2 xs={12} sm={6}>
            <FormInput
              fullWidth
              name="billing.reference"
              label="REFERENCE_BILLING"
            />
          </Grid2>

          <Grid2 xs={12} sm={6}>
            <FormInput
              fullWidth
              name="billing.email"
              label="EMAIL"
              helperText="Email for billing"
            />
          </Grid2>

          <Grid2 xs={12} sm={6}>
            <FormInput
              fullWidth
              name="billing.firstName"
              label="FIRSTNAME"
              helperText="Only for private persons"
            />
          </Grid2>

          <Grid2 xs={12} sm={6}>
            <FormInput
              fullWidth
              name="billing.lastName"
              label="LASTNAME"
              helperText="Only for private persons"
            />
          </Grid2>

          <Grid2 xs={12}>
            <FormInput fullWidth name="billing.address" label="ADDRESS" />
          </Grid2>

          <Grid2 xs={12} sm={4}>
            <FormInput
              fullWidth
              name="billing.postalCode"
              label="POSTAL_CODE"
            />
          </Grid2>

          <Grid2 xs={12} sm={8}>
            <FormInput fullWidth name="billing.city" label="CITY" />
          </Grid2>
        </Grid2>
      </Form>
    </Box>
  );
};

export default InvoiceEdit;
